import useToggle from 'hooks/CustomHooks/useToggle';
import { useEffect, useState } from 'react';
import { useLoginStore } from 'store/useLoginDataStore';
import { BorderColor } from 'styles/Global/colorSemantic';
import { PageTitle, RowDivider } from 'styles/Global/commonStyles';
import { DiagnosisLayout, FilterWrapper, HospitalPageLabel } from 'styles/Global/hospitalStyles';
import { useLoadingStore } from 'store/useLoadingStore';
import { AdminInnerLayout } from 'styles/Global/Admin/adminCommonStyles';
import { GetVoucherListApiListType } from 'models/Admin/AdminVoucherPageTypes';
import { adminManageVoucherPageApi } from 'api/Admin/adminManageVoucherPageApi';
import AdminVoucherListPagination from './AdminVoucherListPagination';
import { AdminVoucherListConstants } from 'constants/Admin/AdminVoucherListPageConstants';
import AdminVoucherFilter from './AdminVoucherFilter';
import VoucherSmsModal from 'components/Admin/Modal/VoucherSmsModal';
import usePaginationStateHooks from 'hooks/CustomHooks/usePaginationStateHooks';

const AdminVoucherListPage = () => {
    const { loginData } = useLoginStore();
    const { setLoading } = useLoadingStore();

    const [voucherData, setVoucherData] = useState<GetVoucherListApiListType[]>();
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [selectedUserData, setSelectedUserData] = useState<GetVoucherListApiListType | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
    const [selectedType, setSelectedType] = useState<number | null>(null);
    const [isSmsModal, setIsSmsModal] = useToggle();

    const { setPage, currentPage, pageGroup, handlePageGroup } = usePaginationStateHooks(voucherData);

    useEffect(() => {
        adminManageVoucherPageApiCall();
    }, [currentPage, selectedStatus, selectedType]);

    const adminManageVoucherPageApiCall = async () => {
        setLoading(true);

        try {
            const response = await adminManageVoucherPageApi.getVoucherList(currentPage, selectedType, selectedStatus, loginData.token);

            if (response.data) {
                setVoucherData(response.data.list);
                setTotalPages(response.data.pagination.totalPages);
                setTotalCount(response.data.pagination.totalCount);
            } else {
                console.log(response);
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    // reset Button function
    const handleResetButton = async () => {
        setSelectedStatus(null);
        setSelectedType(null);
        const response = await adminManageVoucherPageApi.getVoucherList(currentPage, null, null, loginData.token);

        setVoucherData(response.data.list);
        setPage(1);
        handlePageGroup(0);
    };

    const clickSmsSendButton = (voucherData: GetVoucherListApiListType) => {
        const currentTimestamp = Date.now();
        const isExpired = voucherData.expiredTimestamp < currentTimestamp;
        const isNotUnused = voucherData.status !== 0;

        if (isExpired || isNotUnused) {
            return; // 만료되었거나 미사용 상태가 아닌 경우 버튼 비활성화
        }

        setSelectedUserData(voucherData);
        setIsSmsModal();
    };

    // 필터 변경 핸들러
    const handleFilterChange = (status: number | null, type: number | null) => {
        setSelectedStatus(status);
        setSelectedType(type);
        setPage(1);
    };

    const handleSendSms = async (code: string, mobile: string, name: string, description: string) => {
        setLoading(true);
        try {
            const updatedVoucher = await adminManageVoucherPageApi.sendVoucherMessage(code, mobile, name, description, loginData.token);

            // 현재 리스트에서 업데이트된 항목 찾아 업데이트
            setVoucherData((prevData) => {
                if (!prevData) return prevData;

                return prevData.map((item) => {
                    if (item.code === code) {
                        return updatedVoucher;
                    }
                    return item;
                });
            });
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    const handleCreateVoucher = async (type: number, count: number) => {
        setLoading(true);
        try {
            const response = await adminManageVoucherPageApi.createVoucherCode(type, count, loginData.token);
            console.log(response);
            
            // 바우처 생성 성공 시 리스트 새로고침
            if (response) {
                await adminManageVoucherPageApiCall();
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };

    return (
        <DiagnosisLayout>
            {isSmsModal && <VoucherSmsModal toggle={setIsSmsModal} selectedVoucherData={selectedUserData} onSendSms={handleSendSms} />}
            <AdminInnerLayout>
                <HospitalPageLabel onClick={() => window.location.reload()}>{AdminVoucherListConstants.pageCommonConstants.title}</HospitalPageLabel>
                <PageTitle $marginbottom='24px'>{AdminVoucherListConstants.pageCommonConstants.title}</PageTitle>
                <RowDivider $backgroundcolor={BorderColor.color_border_neutral} $marginbottom='16px' />
                <FilterWrapper>
                    <AdminVoucherFilter onFilterChange={handleFilterChange} onReset={handleResetButton} onCreateVoucher={handleCreateVoucher} />
                </FilterWrapper>
                <AdminVoucherListPagination
                    initNumber={voucherData?.length}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageGroup={pageGroup}
                    handlePageGroup={handlePageGroup}
                    handlePageChange={setPage}
                    currentData={voucherData}
                    totalCount={totalCount}
                    clickSmsSendButton={clickSmsSendButton}
                    handleResetButton={handleResetButton}
                />
            </AdminInnerLayout>
        </DiagnosisLayout>
    );
};

export default AdminVoucherListPage;
